import React, { useRef, Suspense, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { Box, Typography, CircularProgress, Stack, LinearProgress } from '@mui/material';
import * as THREE from 'three';
import { SplatMesh } from 'spark';
import { useGetGSplatOutputAssets } from 'api/output/queries/useGetGSplatOutputAssets';
import gsplatLogo from '../../../public/assets/gsplatviewerlogo.svg';

// Import and register shader chunks
import splatDefines from '../../../lib/spark/src/shaders/splatDefines.glsl';

// Register shader chunk with Three.js
if (typeof splatDefines === 'string') {
    (THREE.ShaderChunk as any).splatDefines = splatDefines;
}

type Props = {
    sourceType: "preimage-output";
    sourceId: string;
} | {
    sourceType: "file";
    sourcePath: string;
} | {
    sourceType: "example";
};

const SplatScene = ({ plyUrl, onLoad }: { plyUrl: string; onLoad: () => void }) => {
    const splatRef = useRef<THREE.Group>(null);

    // Create and add the SplatMesh
    React.useEffect(() => {
        if (!splatRef.current || !plyUrl) return;

        const splatMesh = new SplatMesh({ 
            url: plyUrl,
            onLoad: () => {
                onLoad();
            },
        });
        
        // Set initial position and rotation to match previous viewer config
        splatMesh.position.set(0, 0, 0);
        // No rotation needed - default orientation works with the camera setup
        splatMesh.quaternion.set(0, 0, 1, 0);
        
        // Add to the scene via the ref (cast to any due to Three.js version mismatch)
        splatRef.current.add(splatMesh as any);

        return () => {
            splatRef.current?.remove(splatMesh as any);
            splatMesh.dispose();
        };
    }, [plyUrl, onLoad]);

    return <group ref={splatRef} />;
};

const GaussianSplatViewer = (props: Props) => {
    const { sourceType } = props;
    const [isLoading, setIsLoading] = useState(true);

    const { data, isLoading: isLoadingAssetInfo } = useGetGSplatOutputAssets(
        sourceType === "preimage-output" ? props.sourceId : undefined
    );

    const plyUrl = 
        sourceType === "preimage-output" && data?.plyUrl
        ? data.plyUrl
        : sourceType === "file" 
        ? props.sourcePath
        : "/assets/example-gsplat.ply";

    const handleLoad = () => {
        setIsLoading(false);
    };

    // if (isLoadingAssetInfo) {
    //     return <Typography>Loading...</Typography>;
    // }

    if (!plyUrl) {
        return <Typography>Missing ply url</Typography>;
    }

    return (
        <Box
            sx={{
                padding: '1rem',
                width: '100%',
            }}
        >
            {/* <Typography
                variant="h1"
                style={{
                    fontSize: '1.5rem',
                    paddingBottom: '2rem',
                }}
            >
                Experimental Gaussian Splat Viewer
            </Typography> */}
            <Box
                sx={{
                    width: '100%',
                    height: '80vh',
                    borderRadius: '10px',
                    border: '1px solid #cccccc00',
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                <Canvas
                    camera={{
                        position: [0, 1, 0],
                        up: [0, 0, -1],
                        fov: 60,
                        near: 0.1,
                        far: 1000,
                    }}
                    gl={{
                        antialias: true,
                        alpha: true,
                    }}
                >
                    <Suspense
                        fallback={
                            <mesh>
                                <boxGeometry args={[1, 1, 1]} />
                                <meshBasicMaterial color="gray" wireframe />
                            </mesh>
                        }
                    >
                        <SplatScene plyUrl={plyUrl} onLoad={handleLoad} />
                    </Suspense>
                    <OrbitControls
                        enableDamping
                        dampingFactor={0.05}
                        target={[1, 0, 0]}
                    />
                </Canvas>
                {isLoading && (
                    <Stack
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <Stack
                            gap={{ xs: 5, sm: 10 }}
                            alignItems="center"
                        >
                            <Box
                                component="img"
                                sx={{
                                    width: {
                                        xs: 200,
                                        sm: 400,
                                    },
                                    height: '100%',
                                }}
                                alt="Gaussian Splat Viewer Logo"
                                src={gsplatLogo}
                            />
                            <LinearProgress
                                sx={{
                                    height: 12,
                                    width: {
                                        xs: 200,
                                        sm: 400,
                                        md: 600,
                                    },
                                    borderRadius: 5,
                                }}
                            />
                        </Stack>
                    </Stack>
                )}
            </Box>
        </Box>
    );
};

export default GaussianSplatViewer;